import React, { useState, useCallback } from 'react';
import { useCRM } from '../utils/CRMContext';
import { useAuth } from '../utils/AuthContext';
import { Lead, LeadSource } from '../types';
import { collection, addDoc, Timestamp, query, where, getDocs } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { formatLocationForMaps } from '../utils/locationFormatter';
import { geocodeAddress } from '../utils/geocoding';
import { Smartphone, FileText } from 'lucide-react';
import './ImportMetaAds.css';

interface ParsedRow {
  [key: string]: string;
}

interface ImportResult {
  email: string;
  name: string;
  action: 'created' | 'skipped' | 'error';
  reason?: string;
}

const ImportMetaAds: React.FC = () => {
  const { user } = useAuth();
  const [parsedData, setParsedData] = useState<ParsedRow[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [fileName, setFileName] = useState<string>('');
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [results, setResults] = useState<ImportResult[]>([]);
  const [showResults, setShowResults] = useState(false);

  // CSV Parser - handles tabs and commas
  const parseCSVLine = (line: string, delimiter: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === delimiter && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  const parseCSV = (text: string): { headers: string[]; rows: ParsedRow[] } => {
    const lines = text.trim().split('\n');
    
    // Detect delimiter (tab or comma)
    const firstLine = lines[0];
    const delimiter = firstLine.includes('\t') ? '\t' : ',';
    
    const headers = parseCSVLine(lines[0], delimiter);
    const rows: ParsedRow[] = [];
    
    for (let i = 1; i < lines.length; i++) {
      if (lines[i].trim()) {
        const values = parseCSVLine(lines[i], delimiter);
        const row: ParsedRow = {};
        headers.forEach((header, index) => {
          row[header.trim()] = values[index] || '';
        });
        rows.push(row);
      }
    }
    return { headers, rows };
  };

  // Parse Meta Ads date format: "2025-01-20T19:11:00+0000"
  const parseMetaDate = (dateStr: string): Date => {
    if (!dateStr) return new Date();
    try {
      const parsed = new Date(dateStr);
      if (!isNaN(parsed.getTime())) {
        return parsed;
      }
    } catch (e) {
      console.warn('Could not parse date:', dateStr);
    }
    return new Date();
  };

  // Get source based on platform
  const getSourceFromPlatform = (platform: string): LeadSource => {
    const p = platform.toLowerCase().trim();
    if (p === 'fb' || p === 'facebook') {
      return 'facebook-form';
    }
    if (p === 'ig' || p === 'instagram') {
      return 'instagram-form';
    }
    return 'unspecified';
  };

  // Parse full name into first and last name
  const parseName = (fullName: string): { firstName: string; lastName: string } => {
    const parts = fullName.trim().split(/\s+/);
    if (parts.length === 0) {
      return { firstName: 'Unknown', lastName: '' };
    }
    if (parts.length === 1) {
      return { firstName: parts[0], lastName: '' };
    }
    return {
      firstName: parts[0],
      lastName: parts.slice(1).join(' ')
    };
  };

  // Check if lead exists by email
  const checkLeadExists = async (email: string): Promise<boolean> => {
    if (!email || email === 'unknown@import.local') return false;
    
    const useTestLeads = localStorage.getItem('crm_use_test_leads') === 'true';
    const collectionName = useTestLeads ? 'testLeads' : 'leads';
    
    const q = query(collection(db, collectionName), where('email', '==', email.toLowerCase().trim()));
    const snapshot = await getDocs(q);
    return !snapshot.empty;
  };

  // Convert row to lead data
  const rowToLead = (row: ParsedRow): Omit<Lead, 'id'> => {
    const { firstName, lastName } = parseName(row['full name'] || row['full_name'] || '');
    const createdAt = parseMetaDate(row['created_time'] || row['created_at'] || '');
    const source = getSourceFromPlatform(row['platform'] || '');
    
    // Build location from city and state
    const city = row['city'] || '';
    const state = row['state'] || '';
    const location = [city, state].filter(Boolean).join(', ');
    
    // Get project description
    const projectNotes = row['tell_us_about_your_project...'] || 
                        row['tell us about your project...'] ||
                        row['project_description'] || '';
    
    // Meta Ads marketing data
    const metaAdCampaign = row['campaign_name'] || row['Campaign Name'] || '';
    const metaAdSet = row['adset_name'] || row['Adset Name'] || row['ad_set_name'] || '';
    const metaAdName = row['ad_name'] || row['Ad Name'] || '';
    
    const lead: Omit<Lead, 'id'> = {
      firstName: firstName || 'Unknown',
      lastName: lastName || '',
      email: (row['email'] || 'unknown@import.local').toLowerCase().trim(),
      phone: row['phone_number'] || row['phone'] || '',
      company: '',
      location: formatLocationForMaps(location) || '',
      source: source,
      projectType: 'Unspecified',
      budget: 0,
      timeline: 'TBD',
      status: 'new',
      labels: [],
      notes: projectNotes ? `Project Interest: ${projectNotes}` : '',
      archived: false,
      geocoded: false,
      createdAt: createdAt,
      updatedAt: createdAt,
    };
    
    // Add Meta Ads fields only if they have values
    if (metaAdCampaign) lead.metaAdCampaign = metaAdCampaign;
    if (metaAdSet) lead.metaAdSet = metaAdSet;
    if (metaAdName) lead.metaAdName = metaAdName;
    
    return lead;
  };

  const handleFile = useCallback((file: File) => {
    if (!file.name.endsWith('.csv') && !file.name.endsWith('.tsv') && !file.name.endsWith('.txt')) {
      setStatus({ type: 'error', message: 'Please select a CSV or TSV file' });
      return;
    }
    
    setFileName(file.name);
    setStatus(null);
    setResults([]);
    setShowResults(false);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      try {
        const { headers, rows } = parseCSV(text);
        setHeaders(headers);
        setParsedData(rows);
        setStatus({ type: 'info', message: `Parsed ${rows.length} rows from ${file.name}` });
      } catch (err) {
        setStatus({ type: 'error', message: 'Error parsing file. Please check the format.' });
      }
    };
    reader.readAsText(file);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleImport = async () => {
    if (!user) {
      setStatus({ type: 'error', message: 'You must be logged in to import leads' });
      return;
    }

    if (parsedData.length === 0) {
      setStatus({ type: 'error', message: 'No data to import' });
      return;
    }

    setImporting(true);
    setProgress(0);
    setResults([]);
    setShowResults(false);
    
    const useTestLeads = localStorage.getItem('crm_use_test_leads') === 'true';
    const collectionName = useTestLeads ? 'testLeads' : 'leads';
    
    let created = 0;
    let skipped = 0;
    let errors = 0;
    const importResults: ImportResult[] = [];

    for (let i = 0; i < parsedData.length; i++) {
      const row = parsedData[i];
      const leadData = rowToLead(row);
      const fullName = `${leadData.firstName} ${leadData.lastName}`.trim();
      
      try {
        // Check if lead already exists
        const exists = await checkLeadExists(leadData.email);
        
        if (exists) {
          skipped++;
          importResults.push({
            email: leadData.email,
            name: fullName,
            action: 'skipped',
            reason: 'Email already exists in database'
          });
          setStatus({ type: 'info', message: `Skipping ${i + 1}/${parsedData.length}: ${leadData.email} (already exists)` });
        } else {
          // Geocode the location
          let coordinates = { lat: 36.85, lng: -75.4 }; // Default: 50 miles off Norfolk
          let isGeocoded = false;
          
          if (leadData.location) {
            setStatus({ type: 'info', message: `Geocoding ${i + 1}/${parsedData.length}: ${leadData.location.substring(0, 30)}...` });
            try {
              const geocodedCoords = await geocodeAddress(leadData.location);
              if (geocodedCoords) {
                coordinates = geocodedCoords;
                isGeocoded = true;
              }
            } catch (geoErr) {
              console.warn('Geocoding failed for:', leadData.location, geoErr);
            }
          }

          // Create the lead
          await addDoc(collection(db, collectionName), {
            ...leadData,
            coordinates: coordinates,
            geocoded: isGeocoded,
            createdAt: Timestamp.fromDate(leadData.createdAt),
            updatedAt: Timestamp.fromDate(leadData.updatedAt),
          });
          
          created++;
          importResults.push({
            email: leadData.email,
            name: fullName,
            action: 'created'
          });
        }
      } catch (err) {
        console.error('Error importing row', i, err);
        errors++;
        importResults.push({
          email: leadData.email,
          name: fullName,
          action: 'error',
          reason: err instanceof Error ? err.message : 'Unknown error'
        });
      }

      setProgress(Math.round(((i + 1) / parsedData.length) * 100));
    }

    setImporting(false);
    setResults(importResults);
    setShowResults(true);
    setStatus({
      type: errors > 0 ? 'error' : 'success',
      message: `Import complete: ${created} created, ${skipped} skipped (duplicates), ${errors} errors`
    });
  };

  if (!user) {
    return (
      <div className="import-meta-ads">
        <h1>Import Meta Ads Leads</h1>
        <p>Please log in to import leads.</p>
      </div>
    );
  }

  return (
    <div className="import-meta-ads">
      <div className="import-header">
        <h1><Smartphone size={24} style={{ verticalAlign: 'middle', marginRight: 8 }} />Import Meta Ads Leads</h1>
        <p>Import leads from Facebook and Instagram Ad campaigns</p>
      </div>

      <div className="import-instructions">
        <h3>Expected CSV Format</h3>
        <p>Export your leads from Meta Ads Manager. The importer will:</p>
        <ul>
          <li>✓ Check for existing leads by email (prevents duplicates)</li>
          <li>✓ Set source to <strong>Facebook Form</strong> for platform "fb"</li>
          <li>✓ Set source to <strong>Instagram Form</strong> for platform "ig"</li>
          <li>✓ Parse full name into first/last name</li>
          <li>✓ Geocode city/state for map display</li>
          <li>✓ Preserve original created_time from Meta</li>
          <li>✓ Store campaign, ad set, and ad name for marketing analysis</li>
        </ul>
        <div className="expected-columns">
          <strong>Required columns:</strong> email, full name, platform, created_time
          <br />
          <strong>Optional columns:</strong> phone_number, city, state, tell_us_about_your_project...
          <br />
          <strong>Marketing data:</strong> campaign_name, adset_name, ad_name
        </div>
      </div>

      <div 
        className={`drop-zone ${dragOver ? 'drag-over' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        <div className="drop-content">
          <span className="drop-icon">📁</span>
          <p>Drag & drop your Meta Ads CSV here</p>
          <p className="or-text">or</p>
          <label className="file-select-btn">
            Browse Files
            <input 
              type="file" 
              accept=".csv,.tsv,.txt"
              onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
              style={{ display: 'none' }}
            />
          </label>
        </div>
      </div>

      {fileName && (
        <div className="file-info">
          <span className="file-icon"><FileText size={16} /></span>
          <span className="file-name">{fileName}</span>
          <span className="row-count">{parsedData.length} leads found</span>
        </div>
      )}

      {status && (
        <div className={`status-message ${status.type}`}>
          {status.message}
        </div>
      )}

      {importing && (
        <div className="progress-container">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progress}%` }}></div>
          </div>
          <span className="progress-text">{progress}%</span>
        </div>
      )}

      {parsedData.length > 0 && !showResults && (
        <>
          <div className="preview-section">
            <h3>Preview ({Math.min(parsedData.length, 10)} of {parsedData.length} rows)</h3>
            <div className="table-container">
              <table className="preview-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Platform</th>
                    <th>Campaign</th>
                    <th>Ad</th>
                    <th>Location</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedData.slice(0, 10).map((row, i) => {
                    const lead = rowToLead(row);
                    return (
                      <tr key={i}>
                        <td>{`${lead.firstName} ${lead.lastName}`.trim()}</td>
                        <td>{lead.email}</td>
                        <td>
                          <span className={`platform-badge ${row['platform']?.toLowerCase()}`}>
                            {row['platform']?.toUpperCase() === 'FB' ? '📘 FB' : 
                             row['platform']?.toUpperCase() === 'IG' ? '📸 IG' : 
                             row['platform'] || '?'}
                          </span>
                        </td>
                        <td className="campaign-cell" title={lead.metaAdCampaign}>{lead.metaAdCampaign?.substring(0, 25) || '—'}</td>
                        <td className="ad-cell" title={lead.metaAdName}>{lead.metaAdName?.substring(0, 25) || '—'}</td>
                        <td>{lead.location || '—'}</td>
                        <td>{lead.createdAt.toLocaleDateString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="import-actions">
            <button 
              className="import-btn"
              onClick={handleImport}
              disabled={importing}
            >
              {importing ? 'Importing...' : `Import ${parsedData.length} Leads`}
            </button>
            <button 
              className="cancel-btn"
              onClick={() => {
                setParsedData([]);
                setHeaders([]);
                setFileName('');
                setStatus(null);
              }}
              disabled={importing}
            >
              Clear
            </button>
          </div>
        </>
      )}

      {showResults && results.length > 0 && (
        <div className="results-section">
          <h3>Import Results</h3>
          <div className="results-summary">
            <span className="result-stat created">
              ✓ {results.filter(r => r.action === 'created').length} Created
            </span>
            <span className="result-stat skipped">
              ⏭ {results.filter(r => r.action === 'skipped').length} Skipped
            </span>
            <span className="result-stat error">
              ✗ {results.filter(r => r.action === 'error').length} Errors
            </span>
          </div>
          <div className="table-container results-table-container">
            <table className="results-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {results.map((result, i) => (
                  <tr key={i} className={`result-row ${result.action}`}>
                    <td>
                      {result.action === 'created' && <span className="status-icon created">✓</span>}
                      {result.action === 'skipped' && <span className="status-icon skipped">⏭</span>}
                      {result.action === 'error' && <span className="status-icon error">✗</span>}
                    </td>
                    <td>{result.name}</td>
                    <td>{result.email}</td>
                    <td>{result.reason || (result.action === 'created' ? 'Successfully created' : '')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button 
            className="new-import-btn"
            onClick={() => {
              setParsedData([]);
              setHeaders([]);
              setFileName('');
              setStatus(null);
              setResults([]);
              setShowResults(false);
            }}
          >
            Start New Import
          </button>
        </div>
      )}
    </div>
  );
};

export default ImportMetaAds;
